import React, {Component} from "react";
import {Control, Field, Icon, Label, Select} from "bloomer";
import Card from "../../../elements/Card";
import Button from "../../../elements/Button";
import SendGAEvent, {GAEventActions, GAEventModels} from "../../../utils/SendGAEvent";
import ErrorUtils from "../../../utils/ErrorUtils";
import {toast} from "react-toastify";
import SectionName from "../../../elements/SectionName";
import InformationCard from "../../../elements/InformationCard";
import Spacer from "../../../elements/Spacer";
import InputWarning from "../../../elements/InputWarning";
import ModelContentContainer from "../../../elements/ModelContentContainer";
import {getVisionAIResult} from "../../../api/models/VisionAI";

interface IVisionAIModelState {
    loading: boolean;
    selectedOption: string;
    emptyOption: boolean;
    file: any,
    imageUrl: any,
    imageBase64: any,
    fileErrorMessage: string,
    result: any;
    errorMessage: string;
    imageWidth: any;
    imageHeight: any;
    imageNaturalWidth: any;
    imageNaturalHeight: any;
    imageOffsetLeft: any;
    imageOffsetTop: any;
    selectedFaceIndex: number;
    selectedFaceEmotions: any;
}

class VisionAIModel extends Component<{}, IVisionAIModelState> {
    public ALLOWED_FILE_TYPES: any = ['image/jpeg', 'image/png'];
    public ALLOWED_FILE_TYPES_STR: string = this.ALLOWED_FILE_TYPES.join(',');
    public fileInputRef: any = null;
    public options: any = [{
        id: '',
        name: '-'
    }, {
        id: 'detect-faces',
        name: 'Face Detection & Emotion Recognition'
    }, {
        id: 'detect-landmarks',
        name: 'Landmark Detection'
    }, {
        id: 'detect-logos',
        name: 'Logo Detection'
    }, {
        id: 'detect-web',
        name: 'Find Similar Images'
    }];

    public emotionsConfig = {
        VERY_LIKELY: {
            text: 'Very Likely',
            color: 'green',
            sortOrder: 1
        },
        LIKELY: {
            text: 'Likely',
            color: 'green',
            sortOrder: 2
        },
        POSSIBLE: {
            text: 'Possible',
            color: 'goldenrod',
            sortOrder: 3
        },
        UNLIKELY: {
            text: 'Unlikely',
            color: 'maroon',
            sortOrder: 4
        },
        VERY_UNLIKELY: {
            text: 'Very Unlikely',
            color: 'maroon',
            sortOrder: 5
        },
        UNKNOWN: {
            text: 'Unknown',
            sortOrder: 6
        }
    };

    public fileInputStyle = {
        width: '100%'
    };

    public constructor(props: {}) {
        super(props);

        this.state = {
            loading: false,
            selectedOption: "",
            emptyOption: false,
            file: null,
            imageUrl: "",
            imageBase64: "",
            fileErrorMessage: "",
            result: [],
            errorMessage: "",
            imageWidth: null,
            imageHeight: null,
            imageNaturalWidth: null,
            imageNaturalHeight: null,
            imageOffsetLeft: 0,
            imageOffsetTop: 0,
            selectedFaceIndex: -1,
            selectedFaceEmotions: []
        };
    }

    public componentDidMount() {
        SendGAEvent(GAEventActions.VIEW_MODEL, GAEventModels.VISION_AI);
    }

    public getEmotions = (data: any) => {
        return Object.keys(data).map((key: any) => {
            if (key !== 'face_bounds') {
                return {
                    emotion: key,
                    value: data[key]
                }
            }
            return null;
        }).filter(d => d);
    };

    public calculateFaceBounds = (faceBounds: any) => {
        const ratio = this.state.imageWidth / this.state.imageNaturalWidth;

        const width = Math.abs(faceBounds[0][0] - faceBounds[2][0]) * ratio;
        const height = Math.abs(faceBounds[0][1] - faceBounds[2][1]) * ratio;

        const top = faceBounds[0][1] * ratio + this.state.imageOffsetTop;
        const left = faceBounds[0][0] * ratio + this.state.imageOffsetLeft;

        return {
            top,
            left,
            width,
            height
        };
    };

    public handleFileChange = (e: any) => {
        this.setState({
            fileErrorMessage: "",
            selectedFaceIndex: -1,
            selectedFaceEmotions: [],
            result: []
        });

        const file = e.target.files[0];
        if (file) {
            if (this.ALLOWED_FILE_TYPES.includes(file.type)) {

                const url = URL.createObjectURL(file);
                this.setState({
                    file,
                    imageUrl: url
                }, () => {
                    const reader = new FileReader();
                    reader.onloadend = () => {
                        this.setState({
                            imageBase64: reader.result
                        });
                    };
                    reader.readAsDataURL(file);
                });
            } else {
                this.setState({
                    file: null,
                    imageUrl: "",
                    imageBase64: "",
                    fileErrorMessage: "The model only accepts JPEG or PNG format files."
                });
            }
        }
    };

    public handleOptionChange = (e: any) => {
        const value = e.target.value;
        this.setState({
            selectedOption: value,
            emptyOption: false,
            selectedFaceIndex: -1,
            selectedFaceEmotions: [],
            result: []
        });
    };

    public onSubmit = async (e: any) => {
        e.target.blur();

        let valid = true;

        if (this.state.loading) {
            valid = false;
        }

        if (!this.state.file || !this.state.imageBase64 || this.state.fileErrorMessage || !this.state.selectedOption) {
            if (!this.state.file || !this.state.imageBase64) {
                this.setState({
                    fileErrorMessage: "Please select image."
                });
            }

            if (!this.state.selectedOption) {
                this.setState({
                    emptyOption: true
                });
            }

            valid = false;
        }

        if (!valid) {
            this.setState({result: []});
            return;
        }

        this.setState({
            loading: true,
            selectedFaceIndex: -1,
            selectedFaceEmotions: [],
            result: [],
            errorMessage: ""
        });

        let errorOccurred = false;

        const formData = new FormData();
        formData.append("image", this.state.file);
        formData.append("function", this.state.selectedOption);

        const response = await getVisionAIResult(formData).catch((error) => {
            if (ErrorUtils.requestLimitExceeded(error.response)) {
                this.setState({loading: false});
                toast.error(ErrorUtils.messageContent.REQUEST_LIMIT_EXCEEDED);
            } else {
                this.setState({
                    loading: false,
                    errorMessage: "Couldn't process image."
                });
            }
            errorOccurred = true;
        });

        if (errorOccurred) {
            return;
        }

        if (response && response.data) {
            if (this.state.selectedOption === 'detect-faces') {
                if (response.data.length) {
                    this.setState({
                        loading: false,
                        result: response.data
                    });
                } else {
                    this.setState({
                        loading: false,
                        errorMessage: "Faces not detected."
                    });
                }
            } else if (this.state.selectedOption === 'detect-landmarks') {
                if (response.data.length) {
                    this.setState({
                        loading: false,
                        result: response.data
                    });
                } else {
                    this.setState({
                        loading: false,
                        errorMessage: "Landmarks not detected."
                    });
                }
            } else if (this.state.selectedOption === 'detect-logos') {
                if (response.data.length) {
                    this.setState({
                        loading: false,
                        result: response.data
                    });
                } else {
                    this.setState({
                        loading: false,
                        errorMessage: "Logos not detected."
                    });
                }
            } else if (this.state.selectedOption === 'detect-web') {
                const images = response.data.visually_similar_images_found;
                if (images && images.length) {
                    this.setState({
                        loading: false,
                        result: images
                    });
                } else {
                    this.setState({
                        loading: false,
                        errorMessage: "Images not found."
                    });
                }
            }
        } else {
            this.setState({
                loading: false,
                errorMessage: "Couldn't process image."
            });
        }

        SendGAEvent(GAEventActions.CALL_MODEL, GAEventModels.VISION_AI);
    };

    public resetState = async (e: any) => {
        e.target.blur();

        this.setState({
            loading: false,
            selectedOption: "",
            emptyOption: false,
            file: null,
            imageUrl: "",
            imageBase64: "",
            fileErrorMessage: "",
            result: [],
            errorMessage: "",
            imageWidth: null,
            imageHeight: null,
            imageNaturalWidth: null,
            imageNaturalHeight: null,
            imageOffsetLeft: 0,
            imageOffsetTop: 0,
            selectedFaceIndex: -1,
            selectedFaceEmotions: []
        });

        if (this.fileInputRef) {
            this.fileInputRef.value = "";
        }
    };

    public render() {
        return (
            <React.Fragment>
                <ModelContentContainer>
                    <Card>
                        <Field>
                            <Control>
                                <SectionName>Select Image:</SectionName>
                                <input
                                    ref={(el) => this.fileInputRef = el}
                                    type="file"
                                    accept={this.ALLOWED_FILE_TYPES_STR}
                                    onChange={this.handleFileChange}
                                    style={this.fileInputStyle}
                                />
                                {
                                    this.state.fileErrorMessage ? <div>
                                        <InputWarning>{this.state.fileErrorMessage}</InputWarning>
                                    </div> : <></>
                                }
                                {
                                    !this.state.fileErrorMessage && this.state.file && this.state.imageUrl ?
                                        <div style={{
                                            margin: '20px',
                                            textAlign: 'center',
                                            position: 'relative'
                                        }}>
                                            {
                                                this.state.result.length && this.state.selectedOption === 'detect-faces' ?
                                                    <React.Fragment>
                                                        {
                                                            this.state.result.map((item: any, index: any) => {
                                                                const isSelected = index === this.state.selectedFaceIndex;
                                                                const position = this.calculateFaceBounds(item.face_bounds);
                                                                const borderStyle = isSelected ? '3px solid white' : '2px solid gold';
                                                                const boxShadowStyle = isSelected ? '2px 2px black' : '1px 1px black';
                                                                return <div
                                                                    key={index}
                                                                    style={{
                                                                        border: borderStyle,
                                                                        boxShadow: boxShadowStyle,
                                                                        position: 'absolute',
                                                                        top: position.top + 'px',
                                                                        left: position.left + 'px',
                                                                        width: position.width + 'px',
                                                                        height: position.height + 'px',
                                                                        cursor: 'pointer'
                                                                    }}
                                                                    onClick={() => {
                                                                        this.setState({
                                                                            selectedFaceIndex: index,
                                                                            selectedFaceEmotions: this.getEmotions(item)
                                                                        });
                                                                    }}
                                                                />;
                                                            })
                                                        }
                                                    </React.Fragment> : <></>
                                            }
                                            <img
                                                style={{
                                                    pointerEvents: 'none'
                                                }}
                                                alt="Image"
                                                src={this.state.imageUrl}
                                                onLoad={(e: any) => {
                                                    const image = e.target;
                                                    this.setState({
                                                        imageWidth: image.width,
                                                        imageHeight: image.height,
                                                        imageNaturalWidth: image.naturalWidth,
                                                        imageNaturalHeight: image.naturalHeight,
                                                        imageOffsetLeft: image.offsetLeft,
                                                        imageOffsetTop: image.offsetTop
                                                    });
                                                }}
                                            />
                                        </div> : <></>
                                }
                            </Control>
                        </Field>
                        <Spacer/>
                        <Field>
                            <Label>Select Option:</Label>
                            <Control>
                                <Select
                                    value={this.state.selectedOption}
                                    onChange={this.handleOptionChange}
                                >
                                    {
                                        this.options.map((option: any) => {
                                            return <option key={option.id} value={option.id}>{option.name}</option>;
                                        })
                                    }
                                </Select>
                                {
                                    this.state.emptyOption ? <div>
                                        <InputWarning>Please select option.</InputWarning>
                                    </div> : <></>
                                }
                            </Control>
                        </Field>
                        <Spacer/>
                        <div>
                            <Button onClick={this.onSubmit}>Submit</Button>
                            <Button onClick={this.resetState} style={{marginLeft: "5px"}}>Reset</Button>
                        </div>
                        {
                            this.state.loading && <div>
                                <Spacer/>
                                <span>Loading...</span>
                                <Icon className="fas fa-spinner fa-spin"/>
                            </div>
                        }
                        <Spacer/>
                        {
                            !this.state.loading && this.state.result.length && this.state.selectedOption === "detect-faces" ? <React.Fragment>
                                <div>Please click on the detected face to view recognized emotions.</div>
                                <Spacer/>
                            </React.Fragment> : <></>
                        }
                        {
                            !this.state.loading && this.state.result.length
                            && this.state.selectedOption === "detect-faces" && this.state.selectedFaceIndex > -1 ? <div>
                                <SectionName>Emotion:</SectionName>
                                <InformationCard>
                                    {
                                        this.state.selectedFaceEmotions.map((item: any) => {
                                            const config = this.emotionsConfig[item.value];
                                            let text = "";
                                            let color = "";
                                            let sortOrder = 0;
                                            if (config) {
                                                text = config.text;
                                                color = config.color;
                                                sortOrder = config.sortOrder;
                                            }
                                            return {
                                                emotion: item.emotion,
                                                text,
                                                color,
                                                sortOrder
                                            }
                                        }).sort((a: any, b: any) => {
                                            return a.sortOrder > b.sortOrder ? 1 : -1;
                                        }).map((item: any) => {
                                            return <div
                                                key={item.emotion}
                                                style={{marginLeft: '10px'}}
                                            >
                                                {item.emotion}{" - "}
                                                <span style={{color: item.color}}>{item.text}</span>
                                            </div>;
                                        })
                                    }
                                </InformationCard>
                            </div> : <></>
                        }
                        {
                            !this.state.loading && this.state.result.length
                            && this.state.selectedOption === "detect-logos" ? <div>
                                <SectionName>Logos:</SectionName>
                                <InformationCard>
                                    {
                                        this.state.result.map((item: any, index: number) => {
                                            return <div
                                                style={{
                                                    marginLeft: '10px'
                                                }}
                                                key={index}
                                            >{item.description}</div>;
                                        })
                                    }
                                </InformationCard>
                            </div> : <></>
                        }
                        {
                            !this.state.loading && this.state.result.length
                            && this.state.selectedOption === "detect-landmarks" ? <div>
                                <SectionName>Landmarks:</SectionName>
                                <InformationCard>
                                    {
                                        this.state.result.map((item: any, index: number) => {
                                            return <React.Fragment key={index}>
                                                <div style={{marginLeft: '10px'}}>{item.description}</div>
                                                <div style={{marginLeft: '20px'}}>
                                                    {
                                                        item.locations.map((loc: any, locationIndex: number) => {
                                                            const href = `https://www.google.com/maps/?q=@${loc.lat},${loc.lng}`;
                                                            return <span style={{marginRight: '10px'}}
                                                                         key={locationIndex}>
                                                                <a rel="noreferrer" target="_blank" href={href}>
                                                                    Location {locationIndex + 1}
                                                                </a>
                                                            </span>;
                                                        })
                                                    }
                                                </div>
                                            </React.Fragment>;

                                        })
                                    }
                                </InformationCard>
                            </div> : <></>
                        }
                        {
                            !this.state.loading && this.state.result.length && this.state.selectedOption === "detect-web" ?
                                <div>
                                    <SectionName>Similar Images:</SectionName>
                                    <InformationCard>
                                        {
                                            this.state.result.map((src: any, index: number) => {
                                                return <div key={index} style={{textAlign: 'center'}}>
                                                    <a rel="noreferrer" target="_blank" href={src}>
                                                        <img alt="Image" src={src} style={{
                                                            maxHeight: '300px'
                                                        }}/>
                                                    </a>
                                                </div>;

                                            })
                                        }
                                    </InformationCard>
                                </div> : <></>
                        }
                        {
                            !this.state.loading && this.state.errorMessage ? <div>
                                {this.state.errorMessage}
                            </div> : <></>
                        }
                    </Card>
                </ModelContentContainer>
            </React.Fragment>
        );
    }
}

export default VisionAIModel;


// WEBPACK FOOTER //
// ./src/routes/Dashboard/VisionAI/VisionAIModel.tsx